import {
  Email as EmailIcon,
  LocalShipping as LocalShippingIcon,
  NoteAlt as NoteIcon,
  Phone as PhoneIcon,
  Sms as SmsIcon,
} from '@mui/icons-material'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import type { Contact, CrmActivityDomain } from '@otw/models/leads/contacts'
import React, { useEffect, useState } from 'react'
import type { LoggableActivityType } from './ContactActivityLogDialog'
import { CrmActivityDomainPillSlider } from './CrmActivityDomainPillSlider'

/**
 * Result returned to the parent on submit. `contactId` is `null` when the
 * activity isn't tied to a specific member contact (only allowed if the
 * company has zero members or the activity is a generic "note").
 */
export interface ContactCompanyActivityLogSubmitPayload {
  note: string
  contactId: string | null
  domain: CrmActivityDomain
}

interface ContactCompanyActivityLogDialogProps {
  open: boolean
  type: LoggableActivityType | null
  companyName: string
  /** Contacts associated with the company. When empty, the picker is hidden. */
  contacts: Contact[]
  isSubmitting?: boolean
  onClose: () => void
  onSubmit: (payload: ContactCompanyActivityLogSubmitPayload) => Promise<void> | void
}

const ACTIVITY_META: Record<
  LoggableActivityType,
  {
    title: string
    icon: React.ReactNode
    placeholder: string
    accent: 'primary' | 'success' | 'warning' | 'info' | 'secondary'
    /** When true, picking a member contact is required (if any exist). */
    requiresContact: boolean
  }
> = {
  call: {
    title: 'Log a call',
    icon: <PhoneIcon />,
    placeholder: 'What was discussed on the call?',
    accent: 'success',
    requiresContact: true,
  },
  sms: {
    title: 'Log SMS',
    icon: <SmsIcon />,
    placeholder: 'Summarize the text exchange…',
    accent: 'info',
    requiresContact: true,
  },
  email: {
    title: 'Log an email',
    icon: <EmailIcon />,
    placeholder: 'Summarize the email…',
    accent: 'primary',
    requiresContact: true,
  },
  note: {
    title: 'Create a note',
    icon: <NoteIcon />,
    placeholder: 'Jot down a note about this contact…',
    accent: 'warning',
    requiresContact: true,
  },
  shipping_repairs: {
    title: 'Shipping & Repairs',
    icon: <LocalShippingIcon />,
    placeholder: 'Summarize the shipping or repairs update…',
    accent: 'secondary',
    requiresContact: true,
  },
}

const ContactCompanyActivityLogDialog: React.FC<ContactCompanyActivityLogDialogProps> = ({
  open,
  type,
  companyName,
  contacts,
  isSubmitting = false,
  onClose,
  onSubmit,
}) => {
  const theme = useTheme()
  const [note, setNote] = useState('')
  const [domain, setDomain] = useState<CrmActivityDomain>('sales')
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null)

  useEffect(() => {
    if (!open) return
    setNote('')
    setDomain('sales')
    setSelectedContact(contacts.length === 1 ? contacts[0] : null)
  }, [open, type, contacts])

  if (!type) return null
  const meta = ACTIVITY_META[type]
  const accent = theme.palette[meta.accent].main
  const hasContacts = contacts.length > 0
  const contactRequired = meta.requiresContact && hasContacts
  const contactInvalid = contactRequired && !selectedContact
  const canSubmit = note.trim().length > 0 && !contactInvalid

  const handleSubmit = async () => {
    if (!canSubmit) return
    await onSubmit({
      note: note.trim(),
      contactId: selectedContact?.id ?? null,
      domain,
    })
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      disableScrollLock
      disableEnforceFocus
      slotProps={{
        backdrop: { sx: { pointerEvents: 'none' } },
      }}
      sx={{
        pointerEvents: 'none',
        '& .MuiDialog-container': { pointerEvents: 'none' },
        '& .MuiPaper-root': { pointerEvents: 'auto' },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              bgcolor: alpha(accent, 0.15),
              color: accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {meta.icon}
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              {meta.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              For {companyName || 'this company'}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <CrmActivityDomainPillSlider value={domain} onChange={setDomain} />
          {hasContacts ? (
            <Autocomplete
              options={contacts}
              value={selectedContact}
              onChange={(_, value) => setSelectedContact(value)}
              getOptionLabel={(option) => option.name || ''}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Stack>
                    <Typography variant="body2" fontWeight={600}>
                      {option.name || 'Unnamed contact'}
                    </Typography>
                    {option.email || option.phone ? (
                      <Typography variant="caption" color="text.secondary">
                        {[option.email, option.phone].filter(Boolean).join(' • ')}
                      </Typography>
                    ) : null}
                  </Stack>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={
                    meta.requiresContact
                      ? 'Contact at this company'
                      : 'Contact at this company (optional)'
                  }
                  required={contactRequired}
                  error={contactInvalid}
                  helperText={
                    contactInvalid
                      ? 'Pick which contact this activity is for.'
                      : undefined
                  }
                />
              )}
            />
          ) : meta.requiresContact ? (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              This company has no associated contacts yet. The activity will be saved on
              the company without a contact attribution.
            </Alert>
          ) : null}
          <TextField
            autoFocus
            label="Note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
            multiline
            minRows={4}
            placeholder={meta.placeholder}
            required
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || !canSubmit}
        >
          {isSubmitting ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ContactCompanyActivityLogDialog
